import { useState, useEffect, useMemo } from "react";
import { Axis, axisPropsFromTickScale, LEFT, RIGHT } from "react-d3-axis";
import { scaleLinear } from "d3-scale";
import {
  TITLE_LABEL_HEIGHT,
  CENTER_LABEL_HEIGHT,
  TREND_CHART_BAR_WIDTH,
  TREND_CHART_BAR_PADDING_HZ,
  TREND_CHART_BAR_PADDING_VERT,
  COUNT_HEIGHT,
  BARSTACK_PADDING_TOP,
} from "../TrendChartStyles";
import styled from "styled-components/macro";
import {
  darkModeTextColor,
  trendChartTargetLineColor,
} from "../../../../constants/colors";
import useWindowSize from "utils/customHooks/useWindowSize";
import useContainerDimensions from "../../../../utils/useContainerDimensions";
import { Z_INDEX, getIsIe11 } from "../../../../constants/constants";
import { TREND_CHART_CONFIG } from "../../../../constants/customers";
import { ONE_MINUTE_OF_MS } from "../../../../utils/timeUtils";
import {
  useTrendChartBinSize,
  useIsSingleProcessPage,
} from "../../../../utils/customHooks/genericHooks";
import { useFlightTranslation } from "../../../../utils/customHooks/useFlightTranslation";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import {
  useShouldHideProfitCategories,
  useBiggestDurationTrendChartBar,
  useTrendChartBarRangesForBarstacks,
} from "../../../../utils/customHooks/trendChartHooks";
import { useCurrentTrendChartBarOutput } from "./trendChartContentUtils";
import {
  useLotInfoForSingleProcessId,
  useProcessIdOnSingleProcessPage,
} from "components/CustomGridItems/LotInfoBar/lotInfoUtils";
import type { TimeRangeFinite } from "types/types";
import { useTrendChartOutputLabel } from "./TrendChartBar/TrendChartBarCount";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { useLastDataDumpRow } from "components/DataGetter/LastDataDumpRowGetter";
import { useIsDarkMode } from "store/atoms";
import { useMediaQuery } from "@mui/material";

export const TREND_CHART_TARGET_STROKE_DASHARRAY = 10;

export enum axisSidesEnum {
  left = "left",
  right = "right",
}

type TrendChartAxisProps = {
  yAxisTitle: string;
  axisMax: number | null;
  target?: number | null;
  numTicks: number;
  numBarsAcrossWidth: number;
  side: axisSidesEnum;
  shouldHideProfitCategories: boolean;
};
const TrendChartAxis = ({
  yAxisTitle,
  axisMax,
  target = null,
  numTicks,
  numBarsAcrossWidth,
  side, // left, right
  shouldHideProfitCategories,
}: TrendChartAxisProps) => {
  /** how far *down* the chart is the horizontal --- line */
  const percentageAboveTarget =
    target && axisMax && 100 - (target / axisMax) * 100; // line draws from top down, so we need to subract the target percentage from 100

  // to ensure size syncs at different zoom levels without having to refresh,
  // force re-render SizeMe when window resize event is detected
  const [key, setKey] = useState(Math.random());
  const { innerWidth: windowWidth } = useWindowSize();
  useEffect(() => {
    // re-render at end of call stack to ensure we resize the axis AFTER everything else resizes
    const timer = setTimeout(() => {
      setKey(Math.random());
    });
    return () => clearTimeout(timer);
  }, [windowWidth]);

  const [ref, dimensions] = useContainerDimensions();
  const [flightConfig] = useFlightConfig();
  const showCounts = flightConfig.trendChart.showCounts;
  const { height } = dimensions;
  const barHeight =
    (height || 0) -
    TITLE_LABEL_HEIGHT -
    BARSTACK_PADDING_TOP -
    (showCounts ? COUNT_HEIGHT : 0) -
    (shouldHideProfitCategories ? CENTER_LABEL_HEIGHT : 0);

  // https://observablehq.com/@d3/d3-scalelinear
  // d3.scaleLinear creates a scaling function to convert from one measure to another
  // here it's from actual units like km or parts into pixels
  const scale =
    axisMax &&
    scaleLinear()
      .domain([axisMax, 0]) // domain = input range e.g. km, parts
      .range([0, barHeight - 2 * TREND_CHART_BAR_PADDING_VERT]); // range = output range e.g. pixels

  const isIe11 = getIsIe11();
  const isPrint = useMediaQuery("print");
  const [isDarkMode] = useIsDarkMode();
  return (
    <TrendChartAxisStyles
      ref={ref}
      key={key}
      $side={side}
      $shouldHideProfitCategories={shouldHideProfitCategories}
      className={"trendChartBackground"}
      $isIe11={isIe11}
      $axisLabelsOffsetTop={
        TITLE_LABEL_HEIGHT +
        BARSTACK_PADDING_TOP +
        (showCounts ? COUNT_HEIGHT : 0)
      }
      $isDarkMode={isDarkMode}
    >
      {axisMax && barHeight > 0 ? (
        <>
          <div className="axisLabelsWrapper" style={{ height: barHeight }}>
            <svg
              height={barHeight}
              transform={`translate(0, ${TREND_CHART_BAR_PADDING_VERT})`}
            >
              <g
                className="axisGroup"
                transform={`translate(${
                  side === "left"
                    ? (isIe11
                        ? // compensation for ie11 not supporting position: sticky
                          0
                        : 1) * TREND_CHART_BAR_WIDTH
                    : 0
                },
                0)`}
              >
                {percentageAboveTarget ? (
                  <line
                    {...{
                      // mirror horizontally if side === "right"
                      [side === "left" ? "x1" : "x2"]: "0",
                      [side === "left" ? "x2" : "x1"]: `${
                        side === "left" ? "" : "-"
                      }${
                        numBarsAcrossWidth *
                        (TREND_CHART_BAR_WIDTH + 2 * TREND_CHART_BAR_PADDING_HZ)
                      }`,
                    }}
                    y1={`${percentageAboveTarget}%`}
                    y2={`${percentageAboveTarget}%`}
                    transform={`translate(0, ${-TREND_CHART_BAR_PADDING_VERT})`}
                    stroke={isDarkMode ? darkModeTextColor : "black"}
                    pathLength="500"
                    className={"targetDowntimeLine"}
                  />
                ) : null}
                <Axis
                  {...axisPropsFromTickScale(scale, numTicks)}
                  format={tickFormatter}
                  style={{ orient: side === "left" ? LEFT : RIGHT }}
                />
              </g>
            </svg>
            <div
              className={`axisLabel yAxis ${side}`}
              style={{
                width: barHeight,
                transform: `translate(${
                  isPrint ? -220 : 0
                }px,${barHeight}px) rotate(${
                  side === "left" ? -0.25 : 0.25
                }turn)`,
              }}
            >
              {yAxisTitle}
            </div>
          </div>
        </>
      ) : null}
    </TrendChartAxisStyles>
  );
};

function tickFormatter(tick: number) {
  return tick >= 1000 ? tick / 1000 + "k" : tick;
}

const TrendChartAxisStyles = styled.div<{
  $side: "left" | "right";
  $shouldHideProfitCategories: boolean;
  $isIe11: boolean;
  $axisLabelsOffsetTop: number;
  $isDarkMode: boolean;
}>`
  padding: ${TREND_CHART_BAR_PADDING_VERT}px 0;
  background: ${({ $isDarkMode }) =>
    $isDarkMode ? "hsla(0, 0%, 18%, 0.9)" : "hsla(0, 0%, 100%, 0.9)"};
  pointer-events: none;
  min-width: ${TREND_CHART_BAR_WIDTH}px;
  max-width: 100%;
  flex-grow: 0;
  display: flex;
  justify-content: ${props =>
    props.$side === "left" ? "flex-end" : "flex-start"};
  overflow: visible;
  box-sizing: border-box;
  z-index: ${Z_INDEX.L6};
  position: relative;
  position: sticky; /* fall back to relative for ie11 */
  ${props => props.$side}: 0;
  svg {
    width: ${TREND_CHART_BAR_WIDTH}px;
    overflow: visible;
    position: absolute;
  }
  .targetDowntimeLine {
    stroke-dasharray: ${TREND_CHART_TARGET_STROKE_DASHARRAY};
    stroke: ${trendChartTargetLineColor};
  }
  line,
  path {
    stroke: ${({ $isDarkMode }) =>
      $isDarkMode ? darkModeTextColor : "#000000"} !important;
  }
  text {
    fill: ${({ $isDarkMode }) =>
      $isDarkMode ? darkModeTextColor : "#000000"} !important;
  }
  .axisLabelsWrapper {
    width: ${TREND_CHART_BAR_WIDTH}px;
    top: ${props => props.$axisLabelsOffsetTop}px;
    bottom: 0;
    position: absolute;
    .axisLabel {
      position: absolute;
      top: 0;
      text-align: center;
      &.left {
        transform-origin: top left;
        left: ${props => (props.$isIe11 ? -TREND_CHART_BAR_WIDTH : 0)}px;
      }
      &.right {
        transform-origin: top right;
        right: 0;
      }
    }
  }
`;

export function TrendChartLeftAxis() {
  const trendChartBarsRanges = useTrendChartBarRangesForBarstacks();
  const numBars = (trendChartBarsRanges || []).length;
  const ckey = useCustomerKey();
  const shouldHideProfitCategories = useShouldHideProfitCategories();
  const { trendChartBinSizeMs } = useTrendChartBinSize();
  const biggestDurationTrendChartBarRollup = useBiggestDurationTrendChartBar();

  // scale the y-axis to fit the biggest bar, in some cases
  const axisScaleY = biggestDurationTrendChartBarRollup?.duration
    ? biggestDurationTrendChartBarRollup?.duration / trendChartBinSizeMs
    : 1;

  // left Y axis is minutes in each hour
  const leftYAxisMax = (axisScaleY * trendChartBinSizeMs) / ONE_MINUTE_OF_MS;
  const [{ trendChart: trendChartConfig }] = useFlightConfig();
  const processId = useProcessIdOnSingleProcessPage();

  const trendChartTargetDowntime =
    (processId &&
      (trendChartConfig.targetByProcess?.[processId] ??
        TREND_CHART_CONFIG[ckey]?.target?.[processId])) ||
    (trendChartConfig.target ?? TREND_CHART_CONFIG[ckey]?.target); // target can be per-process or overall
  const { translate } = useFlightTranslation();

  return (
    <TrendChartAxis
      shouldHideProfitCategories={shouldHideProfitCategories}
      axisMax={leftYAxisMax}
      numTicks={4}
      numBarsAcrossWidth={numBars}
      yAxisTitle={translate("Time_Distribution_(min)")}
      target={trendChartTargetDowntime}
      side={axisSidesEnum.left}
    />
  );
}

export function TrendChartRightAxis() {
  const trendChartBarsRanges = useTrendChartBarRangesForBarstacks();
  const numBars = (trendChartBarsRanges || []).length;
  const ckey = useCustomerKey();
  const shouldHideProfitCategories = useShouldHideProfitCategories();
  const { trendChartBinSizeMs } = useTrendChartBinSize();
  const biggestDurationTrendChartBarRollup = useBiggestDurationTrendChartBar();

  // scale the y-axis to fit the biggest bar, in some cases
  const axisScaleY = biggestDurationTrendChartBarRollup?.duration
    ? biggestDurationTrendChartBarRollup?.duration / trendChartBinSizeMs
    : 1;
  const lotInfoForProcess = useLotInfoForSingleProcessId();

  const [
    {
      general: { enableLotInfoModule: shouldUseLotProgress },
      trendChart: trendChartConfig,
    },
  ] = useFlightConfig();

  const maxOutputPossible =
    trendChartConfig.maxOutput ?? TREND_CHART_CONFIG[ckey]?.maxOutput;

  // right Y axis is either...
  const rightYAxisMaxIdeal = shouldUseLotProgress
    ? // lot size (batch progress customers) or
      lotInfoForProcess?.lotSize && lotInfoForProcess.lotSize * 1.1 // (add 10% margin in case they go over)
    : // max output possible (other customers)
      maxOutputPossible && maxOutputPossible * axisScaleY;

  const shouldShowHorizontalTargetLine = !trendChartConfig.showTargetTrendLine;

  const { translate } = useFlightTranslation();

  const currentCumulativeOutput = useCurrentTrendChartBarOutput();

  const targetCountForCurrentHour =
    useTargetCountForCurrentHour(trendChartBarsRanges);

  const rightYAxisMax = Math.max(
    currentCumulativeOutput || 0,
    rightYAxisMaxIdeal || 0
  );

  const outputLabel = useTrendChartOutputLabel();
  const rightYAxisTitle = translate(`${outputLabel}_Produced`);

  return (
    <TrendChartAxis
      shouldHideProfitCategories={shouldHideProfitCategories}
      axisMax={rightYAxisMax}
      numTicks={5}
      numBarsAcrossWidth={numBars}
      yAxisTitle={rightYAxisTitle}
      side={axisSidesEnum.right}
      target={shouldShowHorizontalTargetLine ? targetCountForCurrentHour : null}
    />
  );
}

/** for single-process pages, get target count to show the horizontal target line referring to the right axis */
function useTargetCountForCurrentHour(
  trendChartBarsRanges: TimeRangeFinite[]
): number | null {
  const isSingleProcessPage = useIsSingleProcessPage();
  const [lastDataDumpRow] = useLastDataDumpRow();
  return useMemo(() => {
    const targetCountPerHour = lastDataDumpRow?.targetRate;
    const now = Date.now();
    const currentHourIdx = trendChartBarsRanges.findIndex(
      ({ ge, lt }) => ge <= now && now < lt
    );
    return isSingleProcessPage && targetCountPerHour
      ? targetCountPerHour * (currentHourIdx + 1)
      : null;
  }, [isSingleProcessPage, lastDataDumpRow?.targetRate, trendChartBarsRanges]);
}
